import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startOfWeek } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import {
  MEAL_TYPE_KEYS, DINNER_COMPONENT_KEYS,
  addDaysISO, todayISO, resolveDateForDayOfWeek, getClientProfileFlags, getCalorieGoal,
} from '../../utils/recipeUtils';

const DAYS = [1, 2, 3, 4, 5, 6, 0]; // ma..zo, zelfde volgorde als de app

async function getRecipes(mealMoment) {
  const { data, error } = await supabase
    .from('recipes')
    .select('*, recipe_meal_moments!inner(meal_moment)')
    .eq('recipe_meal_moments.meal_moment', mealMoment)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return data || [];
}

export default function WeekschemaPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [isProspect, setIsProspect] = useState(false);
  const [maxKcal, setMaxKcal] = useState(2000);
  const [schedule, setSchedule] = useState(null);
  const [entries, setEntries] = useState([]);
  const [activeDay, setActiveDay] = useState(1);
  const [isRecurring, setIsRecurring] = useState(true);
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeMeal, setActiveMeal] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [dinnerRecipes, setDinnerRecipes] = useState({ protein: [], carbs: [], vegetables: [] });
  const [dinnerSel, setDinnerSel] = useState({ protein: null, carbs: null, vegetables: null });
  const [conflict, setConflict] = useState(null);
  const [removeMeal, setRemoveMeal] = useState(null);

  async function reloadEntries(scheduleId) {
    const { data: ents } = await supabase
      .from('meal_schedule_entries')
      .select('*, recipe:recipes!meal_schedule_entries_recipe_id_fkey(*), dinner_protein:recipes!meal_schedule_entries_dinner_protein_id_fkey(*), dinner_carbs:recipes!meal_schedule_entries_dinner_carbs_id_fkey(*), dinner_vegetables:recipes!meal_schedule_entries_dinner_vegetables_id_fkey(*)')
      .eq('schedule_id', scheduleId);
    setEntries(ents || []);
  }

  async function loadSchedule() {
    try {
      const { data: sched } = await supabase
        .from('meal_schedules')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (sched) {
        setSchedule(sched);
        setIsRecurring(sched.is_recurring);
        setWeekStartDay(sched.week_start_day);
        await reloadEntries(sched.id);
      }
    } catch {
      // Nog geen schema
    } finally {
      setLoading(false);
    }
  }

  // Maakt (indien nog niet aanwezig) het weekschema aan op basis van de
  // huidige, mogelijk nog niet opgeslagen isRecurring/weekStartDay-toggle —
  // gedeeld door saveEntry en de "uitgebreid recept kiezen"-navigatie.
  async function ensureScheduleId() {
    if (schedule?.id) return schedule.id;
    const validFrom = todayISO();
    const { data: newSched, error } = await supabase.from('meal_schedules').insert({
      user_id: user.id, week_start_day: weekStartDay, is_recurring: isRecurring,
      valid_from: validFrom, valid_until: isRecurring ? null : addDaysISO(validFrom, 6),
    }).select().maybeSingle();
    if (error) throw error;
    if (!newSched) throw new Error(t('recipes.weekschemaKonNietWordenAangemaakt'));
    setSchedule(newSched);
    return newSched.id;
  }

  useEffect(() => {
    if (!user?.id) return;
    getClientProfileFlags(user.id).then(f => setIsProspect(f.isProspect));
    getCalorieGoal(user.id).then(setMaxKcal);
    loadSchedule();
  }, [user?.id]);

  function getEntry(dayOfWeek, mealType) { return entries.find(e => e.day_of_week === dayOfWeek && e.meal_type === mealType); }

  function getDayKcal(dayOfWeek) {
    return entries.filter(e => e.day_of_week === dayOfWeek).reduce((sum, e) => {
      if (e.meal_type === 'dinner') return sum + ((e.dinner_protein?.calories || 0) + (e.dinner_carbs?.calories || 0) + (e.dinner_vegetables?.calories || 0));
      return sum + (e.recipe?.calories || 0);
    }, 0);
  }

  async function openPicker(meal) {
    setActiveMeal(meal);
    setDinnerSel({ protein: null, carbs: null, vegetables: null });
    if (meal.key === 'dinner') {
      const all = await getRecipes('dinner');
      setDinnerRecipes({
        protein: all.filter(r => r.dinner_component_type === 'protein'),
        carbs: all.filter(r => r.dinner_component_type === 'carbs'),
        vegetables: all.filter(r => r.dinner_component_type === 'vegetables'),
      });
      const existing = getEntry(activeDay, 'dinner');
      if (existing) setDinnerSel({ protein: existing.dinner_protein_id || null, carbs: existing.dinner_carbs_id || null, vegetables: existing.dinner_vegetables_id || null });
    } else {
      setRecipes(await getRecipes(meal.key));
    }
  }

  async function saveEntry(mealType, recipeId, dinnerProteinId, dinnerCarbsId, dinnerVegetablesId) {
    try {
      const scheduleId = await ensureScheduleId();
      const existing = getEntry(activeDay, mealType);
      if (existing) {
        // Rij-check: geen van de schrijfacties op deze pagina keek naar `error`
        // of naar het aantal geraakte rijen, terwijl een UPDATE/DELETE die door
        // RLS 0 rijen raakt geen fout geeft (BRIEFING hoofdstuk 8). Het scherm
        // herlaadde daarna gewoon en toonde de oude keuze zonder melding.
        const { data: updRow, error: updErr } = await supabase
          .from('meal_schedule_entries')
          .update({ recipe_id: recipeId || null, dinner_protein_id: dinnerProteinId || null, dinner_carbs_id: dinnerCarbsId || null, dinner_vegetables_id: dinnerVegetablesId || null })
          .eq('id', existing.id)
          .select('id')
          .maybeSingle();
        if (updErr) throw updErr;
        if (!updRow) throw new Error(t('common.wijzigingNietOpgeslagen'));
      } else {
        const { error: insErr } = await supabase.from('meal_schedule_entries').insert({ schedule_id: scheduleId, day_of_week: activeDay, meal_type: mealType, recipe_id: recipeId || null, dinner_protein_id: dinnerProteinId || null, dinner_carbs_id: dinnerCarbsId || null, dinner_vegetables_id: dinnerVegetablesId || null });
        if (insErr) throw insErr;
      }
      // reloadEntries (niet loadSchedule): een enkel eetmoment invullen mag het
      // schema-type (eenmalig/herhalend) nooit wijzigen — zie BRIEFING.md hoofdstuk 7.
      await reloadEntries(scheduleId);
      setActiveMeal(null);
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  // Opent het receptdetailscherm voor een uitgebreid recept binnen de
  // weekschema-template-context — save-target meal_schedule_entries i.p.v.
  // daily_meal_logs.
  async function openRecipeDetailForSchedule(recipe, mealType) {
    try {
      const scheduleId = await ensureScheduleId();
      navigate(`/client/voeding/recept/${recipe.id}?mode=kiezen&mealType=${mealType}&saveTarget=schedule_entry&scheduleId=${scheduleId}&dayOfWeek=${activeDay}`);
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  // Verwijdert een gekozen recept (basis of uitgebreid) voor het actieve
  // eetmoment op de actieve dag — weekschema-equivalent van VoedingPage.jsx's
  // confirmRemoveMeal, via de ✕-knop i.p.v. lang indrukken (geen touch-gebaar op web).
  async function confirmRemoveEntry() {
    try {
      const scheduleId = await ensureScheduleId();
      const { data: delRows, error: delErr } = await supabase
        .from('meal_schedule_entries')
        .delete()
        .eq('schedule_id', scheduleId)
        .eq('day_of_week', activeDay)
        .eq('meal_type', removeMeal)
        .select('id');
      if (delErr) throw delErr;
      // De ✕-knop verschijnt alleen bij een bestaande keuze, dus 0 rijen
      // betekent hier altijd dat het verwijderen niet is doorgekomen.
      if (!delRows?.length) throw new Error(t('common.wijzigingNietVerwijderd'));
      setRemoveMeal(null);
      await reloadEntries(scheduleId);
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  async function handleSaveScheduleSettings() {
    try {
      if (schedule) {
        const validUntil = isRecurring ? null : addDaysISO(schedule.valid_from, 6);
        const { data: updRow, error: updErr } = await supabase
          .from('meal_schedules')
          .update({ is_recurring: isRecurring, week_start_day: weekStartDay, valid_until: validUntil })
          .eq('id', schedule.id)
          .select('id')
          .maybeSingle();
        if (updErr) throw updErr;
        if (!updRow) throw new Error(t('common.wijzigingNietOpgeslagen'));
        setSchedule({ ...schedule, is_recurring: isRecurring, week_start_day: weekStartDay, valid_until: validUntil });
      }
      showSuccess(isRecurring ? t('recipes.savedRecurring') : t('recipes.savedOneTime'));
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  async function writeEntriesToDays(targetDays, dayEntries) {
    for (const day of targetDays) {
      for (const entry of dayEntries) {
        const existing = getEntry(day, entry.meal_type);
        if (existing) {
          const { data: updRow, error: updErr } = await supabase
            .from('meal_schedule_entries')
            .update({ recipe_id: entry.recipe_id, dinner_protein_id: entry.dinner_protein_id, dinner_carbs_id: entry.dinner_carbs_id, dinner_vegetables_id: entry.dinner_vegetables_id })
            .eq('id', existing.id)
            .select('id')
            .maybeSingle();
          if (updErr) throw updErr;
          if (!updRow) throw new Error(t('common.wijzigingNietOpgeslagen'));
        } else {
          const { error: insErr } = await supabase.from('meal_schedule_entries').insert({ schedule_id: schedule.id, day_of_week: day, meal_type: entry.meal_type, recipe_id: entry.recipe_id, dinner_protein_id: entry.dinner_protein_id, dinner_carbs_id: entry.dinner_carbs_id, dinner_vegetables_id: entry.dinner_vegetables_id });
          if (insErr) throw insErr;
        }
      }
    }
  }

  async function overwriteMaterializedDates(dates, dayEntries) {
    for (const dateStr of dates) {
      // Alleen een error-check: 0 verwijderde rijen is hier legitiem (een dag
      // zonder eerder gelogde maaltijden), dus een rij-check zou vals alarm
      // geven. De insert hieronder faalt wél hard bij een rechtenprobleem.
      const { error: delErr } = await supabase.from('daily_meal_logs').delete().eq('user_id', user.id).eq('log_date', dateStr);
      if (delErr) throw delErr;
      for (const entry of dayEntries) {
        let totalCalories = 0;
        if (entry.meal_type === 'dinner') {
          totalCalories = (entry.dinner_protein?.calories || 0) + (entry.dinner_carbs?.calories || 0) + (entry.dinner_vegetables?.calories || 0);
          if (!entry.dinner_protein_id && !entry.dinner_carbs_id && !entry.dinner_vegetables_id) continue;
        } else {
          totalCalories = entry.recipe?.calories || 0;
          if (!entry.recipe_id) continue;
        }
        const { error: insErr } = await supabase.from('daily_meal_logs').insert({
          user_id: user.id, log_date: dateStr, meal_type: entry.meal_type,
          recipe_id: entry.recipe_id || null,
          dinner_protein_id: entry.dinner_protein_id || null,
          dinner_carbs_id: entry.dinner_carbs_id || null,
          dinner_vegetables_id: entry.dinner_vegetables_id || null,
          total_calories: totalCalories, is_planned: false, source: 'manual',
        });
        if (insErr) throw insErr;
      }
    }
  }

  async function copyToAllDays() {
    const dayEntries = entries.filter(e => e.day_of_week === activeDay);
    let otherDays = DAYS.filter(d => d !== activeDay);

    if (!isRecurring && schedule) {
      const today = todayISO();
      otherDays = otherDays.filter(d => {
        const dateStr = resolveDateForDayOfWeek(d, schedule, startOfWeek, isRecurring);
        return !dateStr || dateStr >= today;
      });
    }

    const dates = otherDays.map(d => ({ day: d, dateStr: resolveDateForDayOfWeek(d, schedule, startOfWeek, isRecurring) })).filter(x => x.dateStr);
    let conflictDays = [];
    if (dates.length > 0) {
      const { data: existingLogs } = await supabase
        .from('daily_meal_logs')
        .select('log_date')
        .eq('user_id', user.id)
        .in('log_date', dates.map(x => x.dateStr));
      const conflictDates = new Set((existingLogs || []).map(l => l.log_date));
      conflictDays = dates.filter(x => conflictDates.has(x.dateStr)).map(x => x.day);
    }

    if (conflictDays.length === 0) {
      try {
        await writeEntriesToDays(otherDays, dayEntries);
        // reloadEntries (niet loadSchedule): kopiëren mag het schema-type nooit
        // wijzigen — zie BRIEFING.md hoofdstuk 7.
        await reloadEntries(schedule.id);
        showSuccess(t('recipes.copiedMsg'));
      } catch (err) {
        showError(t('recipes.foutOpslaanKeuze') + err.message);
      }
      return;
    }

    setConflict({ conflictDays, otherDays, dayEntries });
  }

  async function handleConflictOverwrite() {
    const { conflictDays, otherDays, dayEntries } = conflict;
    try {
      const dates = conflictDays.map(d => resolveDateForDayOfWeek(d, schedule, startOfWeek, isRecurring)).filter(Boolean);
      await overwriteMaterializedDates(dates, dayEntries);
      await writeEntriesToDays(otherDays, dayEntries);
      setConflict(null);
      await reloadEntries(schedule.id);
      showSuccess(t('recipes.copiedMsg'));
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  async function handleConflictKeepExisting() {
    const { conflictDays, otherDays, dayEntries } = conflict;
    try {
      const conflictSet = new Set(conflictDays);
      const safeDays = otherDays.filter(d => !conflictSet.has(d));
      await writeEntriesToDays(safeDays, dayEntries);
      setConflict(null);
      await reloadEntries(schedule.id);
      showSuccess(t('recipes.copiedMsg'));
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  const dayKcal = getDayKcal(activeDay);

  if (loading) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  if (isProspect) {
    return (
      <div>
        <div className="page-header"><h1 className="page-title">{t('recipes.weekschemaTitle')}</h1></div>
        <div className="card">
          <p style={{ margin: 0, fontWeight: 600, color: '#f59e0b' }}>{t('recipes.membershipRequiredTitel')}</p>
          <p className="text-muted" style={{ marginTop: 6, marginBottom: 0 }}>{t('recipes.membershipRequiredTekst')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('recipes.weekschemaTitle')}</h1>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>{t('recipes.settingsTitle')}</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <button
            className={`btn ${isRecurring ? 'btn-primary' : 'btn-ghost'}`}
            style={{ flex: '1 1 160px' }}
            onClick={() => setIsRecurring(true)}
          >
            {t('recipes.recurring')} <span className="text-sm" style={{ display: 'block', fontWeight: 400 }}>{t('recipes.recurringDesc')}</span>
          </button>
          <button
            className={`btn ${!isRecurring ? 'btn-primary' : 'btn-ghost'}`}
            style={{ flex: '1 1 160px' }}
            onClick={() => setIsRecurring(false)}
          >
            {t('recipes.oneTime')} <span className="text-sm" style={{ display: 'block', fontWeight: 400 }}>{t('recipes.oneTimeDesc')}</span>
          </button>
        </div>
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label">{t('recipes.startDayLabel')}</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DAYS.map(d => (
              <button
                key={d}
                type="button"
                className={`btn btn-sm ${weekStartDay === d ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setWeekStartDay(d)}
              >
                {t(`recipes.dagKort.${d}`)}
              </button>
            ))}
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleSaveScheduleSettings}>{t('recipes.saveSettings')}</button>
      </div>

      <h3 className="text-sm font-semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
        {t('recipes.daySection')}
      </h3>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {DAYS.map(d => {
          const kcal = getDayKcal(d);
          const filled = entries.some(e => e.day_of_week === d);
          const isActive = activeDay === d;
          const dateStr = !isRecurring && schedule ? resolveDateForDayOfWeek(d, schedule, startOfWeek, isRecurring) : null;
          const isPast = dateStr && dateStr < todayISO();
          return (
            <button
              key={d}
              className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-ghost'}`}
              style={{ opacity: isPast ? 0.4 : 1, borderColor: !isActive && filled ? 'var(--color-primary)' : undefined }}
              onClick={() => setActiveDay(d)}
            >
              {t(`recipes.dagKort.${d}`)}{filled ? ` · ${kcal}` : ''}
            </button>
          );
        })}
      </div>

      <div className="card" style={{ padding: 0, marginBottom: 12 }}>
        {MEAL_TYPE_KEYS.map((meal, i) => {
          const entry = getEntry(activeDay, meal.key);
          let subText = t('recipes.notSet');
          let kcalText = '';
          if (entry) {
            if (meal.key === 'dinner') {
              const parts = [entry.dinner_protein?.name, entry.dinner_carbs?.name, entry.dinner_vegetables?.name].filter(Boolean);
              subText = parts.join(' · ') || t(`recipes.${meal.label}`);
              kcalText = ((entry.dinner_protein?.calories || 0) + (entry.dinner_carbs?.calories || 0) + (entry.dinner_vegetables?.calories || 0)) + ' kcal';
            } else {
              subText = entry.recipe?.name || '';
              kcalText = (entry.recipe?.calories || 0) + ' kcal';
            }
          }
          function openEntry() {
            openPicker(meal);
          }
          return (
            <div
              key={meal.key}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: entry ? '#e8f5e9' : 'var(--color-bg)', cursor: 'pointer',
              }} onClick={openEntry}>
                {entry ? '✓' : '+'}
              </div>
              <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={openEntry}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{t(`recipes.${meal.label}`)}</div>
                <div className="text-muted text-sm" style={{ fontStyle: entry ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subText}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                {kcalText && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)' }}>{kcalText}</span>}
                {entry && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setRemoveMeal(meal.key)} title={t('recipes.removeMealAction')}>✕</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span className="text-sm text-muted">{t('recipes.totalLabel', { day: t(`recipes.dagVol.${activeDay}`) })}</span>
        <span className="text-sm font-semibold" style={{ color: dayKcal > maxKcal ? '#e24b4a' : 'var(--color-primary)' }}>{dayKcal} / {maxKcal} kcal</span>
      </div>

      <button className="btn btn-secondary" style={{ width: '100%' }} onClick={copyToAllDays}>
        {t('recipes.copyBtn', { day: t(`recipes.dagVol.${activeDay}`) })}
      </button>

      {activeMeal && (
        <div className="modal-overlay" onClick={() => setActiveMeal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t(`recipes.${activeMeal.label}`)}</h2>
              <button className="modal-close" onClick={() => setActiveMeal(null)}>✕</button>
            </div>
            {activeMeal.key === 'dinner' ? (
              <DinnerPicker dinnerRecipes={dinnerRecipes} dinnerSel={dinnerSel} setDinnerSel={setDinnerSel}
                onSave={() => saveEntry('dinner', null, dinnerSel.protein, dinnerSel.carbs, dinnerSel.vegetables)} />
            ) : (
              <div>
                {recipes.length === 0 && <div className="empty-state"><p>{t('recipes.noRecipes')}</p></div>}
                {recipes.map((r, i) => {
                  const isUitgebreid = r.recipe_type === 'uitgebreid';
                  const isCurrent = r.id === getEntry(activeDay, activeMeal?.key)?.recipe?.id;
                  return (
                    <div
                      key={r.id}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 4px', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none', cursor: 'pointer', background: isCurrent ? '#e8f5e9' : undefined }}
                      onClick={() => { if (isUitgebreid) { openRecipeDetailForSchedule(r, activeMeal.key); } else { saveEntry(activeMeal.key, r.id, null, null, null); } }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 14 }}>{r.name}</span>
                          {isUitgebreid && <span className="badge">{t('recipes.badgeExtended')}</span>}
                          {isCurrent && <span style={{ color: 'var(--color-primary)', fontSize: 14 }}>✓</span>}
                        </div>
                        {r.description && <div className="text-muted text-sm">{r.description}</div>}
                        {isCurrent && <div className="text-sm" style={{ color: 'var(--color-primary)' }}>{t('recipes.currentChoice')}</div>}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)', flexShrink: 0 }}>{r.calories} kcal</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {removeMeal && (
        <div className="modal-overlay" onClick={() => setRemoveMeal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('recipes.removeMealTitel')}</h2>
              <button className="modal-close" onClick={() => setRemoveMeal(null)}>✕</button>
            </div>
            <p className="text-muted" style={{ marginBottom: 20 }}>{t('recipes.removeMealTekst')}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setRemoveMeal(null)}>{t('recipes.annuleren')}</button>
              <button className="btn btn-danger" onClick={confirmRemoveEntry}>{t('recipes.removeMealAction')}</button>
            </div>
          </div>
        </div>
      )}

      {conflict && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 className="modal-title">{t('recipes.conflictTitel')}</h2>
            </div>
            <p className="text-muted" style={{ marginBottom: 20 }}>
              {t('recipes.conflictTekst', { count: conflict.conflictDays.length })}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleConflictOverwrite}>
                {t('recipes.conflictOverwrite', { count: conflict.conflictDays.length })}
              </button>
              <button className="btn btn-secondary" onClick={handleConflictKeepExisting}>
                {t('recipes.conflictKeepExisting')}
              </button>
              <button className="btn btn-ghost" onClick={() => setConflict(null)}>
                {t('recipes.annuleren')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DinnerPicker({ dinnerRecipes, dinnerSel, setDinnerSel, onSave }) {
  const allSelected = dinnerSel.protein && dinnerSel.carbs && dinnerSel.vegetables;
  return (
    <div>
      <div className="card" style={{ background: 'rgba(245,158,11,0.08)', borderColor: '#f59e0b', marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#b45309' }}>{t('recipes.dinnerHintText')}</p>
      </div>
      {Object.entries(DINNER_COMPONENT_KEYS).map(([type, labelKey]) => (
        <div key={type} style={{ marginBottom: 14 }}>
          <div className="text-sm font-semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6, color: 'var(--color-text-muted)' }}>
            {t(`recipes.${labelKey}Label`)}
          </div>
          {(dinnerRecipes[type] || []).map((r, i) => {
            const selected = dinnerSel[type] === r.id;
            return (
              <div
                key={r.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  padding: '10px 4px', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
                  background: selected ? '#e8f5e9' : 'transparent', cursor: 'pointer',
                }}
                onClick={() => setDinnerSel(prev => ({ ...prev, [type]: r.id }))}
              >
                <span style={{ fontSize: 14, fontWeight: selected ? 600 : 400, color: selected ? '#1b5e20' : 'inherit' }}>{r.name}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)' }}>{r.calories} kcal {selected ? '✓' : ''}</span>
              </div>
            );
          })}
        </div>
      ))}
      <button className="btn btn-primary" style={{ width: '100%' }} disabled={!allSelected} onClick={onSave}>
        {t('recipes.saveDinner')}
      </button>
    </div>
  );
}
